import type { ReactElement } from 'react';
import { useEffect, useRef, useState } from 'react';
import type { Organization, TileKind, UpdateOrgBody } from '@contracts/orgs';
import {
  useDeleteWorkspace,
  useMe,
  useOrg,
  useOrgTiles,
  useSetOrgTile,
  useSetRole,
  useUpdateOrg,
} from '../api/queries';
import { toTile } from '../lib/tile-image';
import {
  Broken,
  Button,
  Card,
  Field,
  Icon,
  Modal,
  Pill,
  Picker,
  SkeletonRows,
  Switch,
  useNotices,
} from '../ui';
import { initialsOf } from '../ui';
import { since } from '../lib/time';
import { SettingsLayout } from '../settings/SettingsLayout';
import { WRITTEN_IN } from '../lib/written-in';

/**
 * Board 09 — people and roles: three roles in the organisation, three in a
 * space, and nothing else to learn. The table says who is what and when they
 * were last here; the matrix beneath says what each role may do, and states
 * only what the server enforces (`orgs/workspace-rules.ts`).
 */
export function Organisation(): ReactElement {
  const org = useOrg();
  return (
    <SettingsLayout
      title="People and roles"
      sub={
        org.data
          ? `${org.data.members.length} ${org.data.members.length === 1 ? 'member' : 'members'} · ${org.data.members.filter((m) => m.role !== 'MEMBER').length} can administer${org.data.domain ? ` · anyone signing in with an @${org.data.domain} address joins here` : ''}`
          : undefined
      }
    >
      {org.isPending ? (
        <SkeletonRows rows={4} />
      ) : org.isError ? (
        <Card flush>
          <Broken what="The organisation" retry={() => void org.refetch()} />
        </Card>
      ) : (
        <PeopleTable org={org.data} />
      )}
      <Card
        title="What each role can do"
        aside={
          <span className="t-secondary">organisation roles · a space adds its own two on top</span>
        }
        flush
      >
        <div className="scroll-x">
          <table className="table">
            <thead>
              <tr>
                <th />
                <th style={{ textAlign: 'center' }}>Owner</th>
                <th style={{ textAlign: 'center' }}>Admin</th>
                <th style={{ textAlign: 'center' }}>Member</th>
              </tr>
            </thead>
            <tbody>
              {(
                [
                  ['Open meetings they attended', true, true, true],
                  ['Create spaces and folders', true, true, true],
                  ['Share a meeting they manage — by name, by space, or by link', true, true, true],
                  [
                    'Set the organisation’s defaults — its name, its notetaker, how long video and transcripts are kept',
                    true,
                    true,
                    false,
                  ],
                  ['Make somebody an admin, or a member again', true, true, false],
                  ['Be demoted here', false, true, true],
                ] as Array<[string, boolean, boolean, boolean]>
              ).map(([what, o, a, m]) => (
                <tr key={what}>
                  <td>{what}</td>
                  {[o, a, m].map((yes, i) => (
                    <td key={i} style={{ textAlign: 'center' }} className="mono">
                      {yes ? (
                        <span className="check-mark">
                          <Icon name="check" size={14} />
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="t-secondary" style={{ padding: '12px 20px', margin: 0 }}>
          An owner is never demoted here; ownership is handed on, not assigned. The last
          administrator stays, so the organisation is never left with nobody to run it.
        </p>
      </Card>
    </SettingsLayout>
  );
}

function PeopleTable({ org }: { org: Organization }): ReactElement {
  const me = useMe();
  const setRole = useSetRole();
  const { show } = useNotices();
  const admin = org.canAdminister;
  return (
    <Card flush>
      <div className="scroll-x">
        <table className="table">
          <thead>
            <tr>
              <th>Person</th>
              <th>Role</th>
              <th className="num">Last seen</th>
            </tr>
          </thead>
          <tbody>
            {org.members.map((m) => (
              <tr key={m.userId}>
                <td>
                  <span className="cluster" style={{ gap: 10, flexWrap: 'nowrap' }}>
                    <span className="avatar">{initialsOf(m.name, m.email)}</span>
                    <span className="stack" style={{ gap: 1 }}>
                      <span className="t-ui">
                        {m.name ?? m.email}
                        {m.email === me.data?.email ? (
                          <span className="t-secondary"> · you</span>
                        ) : null}
                      </span>
                      <span className="mono address">{m.email}</span>
                    </span>
                  </span>
                </td>
                <td>
                  {admin && m.role !== 'OWNER' ? (
                    <Picker
                      aria-label={`Role of ${m.email}`}
                      value={m.role}
                      onChange={(role) =>
                        setRole.mutate(
                          { userId: m.userId, role },
                          {
                            onSuccess: () =>
                              show({
                                tone: 'success',
                                text: `${m.name ?? m.email} is ${role === 'ADMIN' ? 'an admin' : 'a member'} now.`,
                              }),
                            onError: (err) => show({ tone: 'failure', text: err.message }),
                          },
                        )
                      }
                      size="sm"
                      width={130}
                      options={[
                        { value: 'ADMIN' as const, label: 'Admin' },
                        { value: 'MEMBER' as const, label: 'Member' },
                      ]}
                    />
                  ) : (
                    <Pill tone={m.role === 'OWNER' ? 'green' : undefined}>
                      {m.role === 'OWNER' ? 'Owner' : m.role === 'ADMIN' ? 'Admin' : 'Member'}
                    </Pill>
                  )}
                </td>
                <td className="num mono">
                  {m.lastActiveAt
                    ? Date.now() - Date.parse(m.lastActiveAt) < 5 * 60_000
                      ? 'now'
                      : `${since(m.lastActiveAt)}`
                    : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

/** The organisation's own defaults, each a live control for an administrator and a fact for everyone else. */
export function RecordingDefaults(): ReactElement {
  const org = useOrg();
  return (
    <SettingsLayout
      title="Recording defaults"
      sub={
        org.data
          ? `${org.data.name} · what every notetaker is called, how long recordings are kept, and what is counted`
          : undefined
      }
    >
      {org.isPending ? (
        <SkeletonRows rows={3} />
      ) : org.isError ? (
        <Card flush>
          <Broken what="The organisation" retry={() => void org.refetch()} />
        </Card>
      ) : (
        <>
          <Defaults org={org.data} />
          <NotetakerTiles admin={org.data.canAdminister} />
          <Privacy org={org.data} />
          {org.data.canDelete ? <DeleteWorkspace org={org.data} /> : null}
        </>
      )}
    </SettingsLayout>
  );
}

function Defaults({ org }: { org: Organization }): ReactElement {
  const update = useUpdateOrg();
  const { show } = useNotices();
  const [name, setName] = useState(org.name);
  const [botName, setBotName] = useState(org.botDisplayName ?? '');
  const [days, setDays] = useState(String(org.videoRetentionDays));
  useEffect(() => {
    setName(org.name);
    setBotName(org.botDisplayName ?? '');
    setDays(String(org.videoRetentionDays));
  }, [org]);
  const admin = org.canAdminister;
  const save = (body: UpdateOrgBody) =>
    update.mutate(body, {
      onSuccess: () =>
        show({ tone: 'success', text: 'Saved — for everyone, from the next meeting on.' }),
      onError: (e) => show({ tone: 'failure', text: e.message }),
    });
  const blurOnEnter = (e: React.KeyboardEvent<HTMLInputElement>) =>
    e.key === 'Enter' && (e.target as HTMLInputElement).blur();
  return (
    <Card title="Organisation" flush>
      <div className="setting">
        <span className="k">Name</span>
        <div className="v">
          {admin ? (
            <Field
              aria-label="Organisation name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onBlur={() => name.trim() && name.trim() !== org.name && save({ name: name.trim() })}
              onKeyDown={blurOnEnter}
              style={{ width: 260 }}
            />
          ) : (
            <span className="t-ui">{org.name}</span>
          )}
          {org.domain ? <span className="why">@{org.domain}</span> : null}
        </div>
      </div>
      {/* ⚠ The workspace's, not a person's. It was a personal setting until
          2026-09-22, which let two colleagues write into one body of knowledge
          in two languages — and knowledge is the one thing here nobody owns
          alone (Vladimir: "move knowledge language selection to the
          organization level, not personal level"). */}
      <div className="setting">
        <span className="k">Knowledge written in</span>
        <div className="v">
          {admin ? (
            <Picker
              aria-label="Knowledge written in"
              value={org.knowledgeLanguage}
              onChange={(knowledgeLanguage) => save({ knowledgeLanguage })}
              width={260}
              options={WRITTEN_IN.map(([value, label]) => ({ value, label }))}
            />
          ) : (
            <span className="t-ui">
              {WRITTEN_IN.find(([code]) => code === org.knowledgeLanguage)?.[1] ??
                org.knowledgeLanguage}
            </span>
          )}
          <span className="why">what sticks in the workspace, for everyone</span>
        </div>
      </div>
      <div className="setting">
        <span className="k">Notetaker name for everyone</span>
        <div className="v">
          {admin ? (
            <Field
              aria-label="Notetaker name for everyone"
              placeholder="Each person’s own name"
              value={botName}
              onChange={(e) => setBotName(e.target.value)}
              onBlur={() => {
                const next = botName.trim() || null;
                if (next !== org.botDisplayName) save({ botDisplayName: next });
              }}
              onKeyDown={blurOnEnter}
              style={{ width: 260 }}
            />
          ) : (
            <span className="t-ui">{org.botDisplayName ?? 'each person’s own name'}</span>
          )}
          <span className="why">
            empty means each person’s notetaker carries their own name, which is what the other side
            of the call expects
          </span>
        </div>
      </div>
      <div className="setting">
        <span className="k">Notetaker speaks</span>
        <div className="v">
          {admin ? (
            <Switch
              aria-label="Notetaker speaks"
              label={org.botVoice ? 'On' : 'Off'}
              checked={org.botVoice ?? false}
              disabled={update.isPending}
              onChange={(e) => save({ botVoice: e.target.checked })}
            />
          ) : (
            <span className="t-ui">{org.botVoice ? 'On' : 'Off'}</span>
          )}
          <span className="why">
            when on, the notetaker says a few fixed phrases out loud in the call — “Screenshot
            taken”, “Asana task created” — in English. It always answers in the meeting chat,
            whatever this says.
          </span>
        </div>
      </div>
      <div className="setting">
        <span className="k">Keep video for</span>
        <div className="v">
          {admin ? (
            <Field
              aria-label="Keep video for, in days"
              type="number"
              min={1}
              max={3650}
              value={days}
              onChange={(e) => setDays(e.target.value)}
              onBlur={() => {
                const n = Math.max(1, Math.min(3650, Number(days) || org.videoRetentionDays));
                setDays(String(n));
                if (n !== org.videoRetentionDays) save({ videoRetentionDays: n });
              }}
              onKeyDown={blurOnEnter}
              style={{ width: 100 }}
            />
          ) : (
            <span className="t-ui">{org.videoRetentionDays}</span>
          )}
          <span className="why">
            days · the video goes after this; how long the words stay is the setting below
          </span>
        </div>
      </div>
      <div className="setting">
        <span className="k">Keep transcripts for</span>
        <div className="v">
          {admin ? (
            <Picker
              aria-label="Keep transcripts for"
              value={
                org.transcriptRetentionDays === null ? '' : String(org.transcriptRetentionDays)
              }
              onChange={(days) => save({ transcriptRetentionDays: days ? Number(days) : null })}
              size="sm"
              width={170}
              options={retentionChoices(org.transcriptRetentionDays).map(([days, words]) => ({
                value: String(days),
                label: words,
              }))}
            />
          ) : (
            <span className="t-ui">{retentionWords(org.transcriptRetentionDays)}</span>
          )}
          <span className="why">
            {org.transcriptRetentionDays === null
              ? 'the words people said stay as long as the meeting does'
              : 'after this the words people said are removed — the written note, its commitments and its decisions stay'}
          </span>
        </div>
      </div>
      {!admin ? (
        <div className="setting">
          <span className="k" />
          <div className="v">
            <span className="why">Only an administrator changes these.</span>
          </div>
        </div>
      ) : null}
    </Card>
  );
}

const RETENTION: Array<[number, string]> = [
  [30, '30 days'],
  [90, '90 days'],
  [180, '6 months'],
  [365, '1 year'],
  [730, '2 years'],
];

/**
 * What the notetaker shows on its camera tile in a call — Ptitim's dark
 * defaults, or the workspace's own (2026-09-30). Vladimir: *"change default
 * images that bot shows during the call to something more neutral and dark and
 * add the solution to change these images to the corporate ones inside the
 * organization settings"*.
 */
function NotetakerTiles({ admin }: { admin: boolean }): ReactElement {
  const tiles = useOrgTiles();
  return (
    <Card
      title="The notetaker in the call"
      aside={<span className="t-secondary">what everybody in your calls sees on its camera</span>}
    >
      {tiles.isPending ? (
        <SkeletonRows rows={2} />
      ) : tiles.isError ? (
        <Broken what="The pictures" retry={() => void tiles.refetch()} />
      ) : (
        <div className="stack">
          <div className="tiles">
            <TileSlot
              kind="recording"
              label="While recording"
              tile={tiles.data.recording}
              admin={admin}
            />
            <TileSlot kind="paused" label="While paused" tile={tiles.data.paused} admin={admin} />
          </div>
          <span className="t-secondary">
            {admin
              ? 'Any picture works: it is fitted into 1280 × 720 without cropping, on the same dark ground. Keep what matters in the middle — some meeting apps crop the edges. Used from the next meeting on.'
              : 'Only an administrator can change these.'}
          </span>
        </div>
      )}
    </Card>
  );
}

function TileSlot({
  kind,
  label,
  tile,
  admin,
}: {
  kind: TileKind;
  label: string;
  tile: { custom: boolean; image: string };
  admin: boolean;
}): ReactElement {
  const set = useSetOrgTile();
  const { show } = useNotices();
  const input = useRef<HTMLInputElement>(null);
  const [fitting, setFitting] = useState(false);
  const save = (image: string | null) =>
    set.mutate(
      { kind, image },
      {
        onSuccess: () =>
          show({
            tone: 'success',
            text:
              image === null
                ? 'Back to Ptitim’s picture — from the next meeting on.'
                : 'Saved — every notetaker shows it from the next meeting on.',
          }),
        onError: (e) => show({ tone: 'failure', text: e.message }),
      },
    );
  const chosen = async (file: File | undefined) => {
    if (!file) return;
    setFitting(true);
    try {
      save(await toTile(file));
    } catch (e) {
      show({ tone: 'failure', text: (e as Error).message });
    } finally {
      setFitting(false);
      if (input.current) input.current.value = '';
    }
  };
  return (
    <figure className="tile-slot" aria-label={label}>
      <img
        className="tile-preview"
        src={tile.image}
        alt={`${label}: ${tile.custom ? 'your own picture' : 'Ptitim’s default'}`}
      />
      <figcaption className="cluster" style={{ gap: 8 }}>
        <span className="t-ui">{label}</span>
        <Pill tone={tile.custom ? 'green' : undefined}>{tile.custom ? 'your own' : 'default'}</Pill>
      </figcaption>
      {admin ? (
        <div className="cluster" style={{ gap: 8 }}>
          <input
            ref={input}
            type="file"
            accept="image/*"
            hidden
            aria-label={`${label}: choose a picture`}
            onChange={(e) => void chosen(e.target.files?.[0])}
          />
          <Button
            size="sm"
            onClick={() => input.current?.click()}
            working={fitting || set.isPending ? 'Saving…' : false}
          >
            Upload a picture…
          </Button>
          {tile.custom ? (
            <Button size="sm" variant="quiet" onClick={() => save(null)} disabled={set.isPending}>
              Use the default
            </Button>
          ) : null}
        </div>
      ) : null}
    </figure>
  );
}

function retentionWords(days: number | null): string {
  if (days === null) return 'as long as the meeting exists';
  return RETENTION.find(([n]) => n === days)?.[1] ?? `${days} days`;
}

/** The presets, plus whatever the workspace already has if it is not one of them. */
function retentionChoices(current: number | null): Array<[string, string]> {
  const choices: Array<[string, string]> = [['', 'As long as the meeting exists']];
  for (const [days, words] of RETENTION) choices.push([String(days), words]);
  if (current !== null && !RETENTION.some(([n]) => n === current)) {
    choices.push([String(current), `${current} days`]);
  }
  return choices;
}

/**
 * What the workspace counts about its people. One switch, off by default, and
 * the sentence beside it is the whole of what turning it on shows — so an
 * administrator can tell their colleagues, which the law expects of them.
 */
function Privacy({ org }: { org: Organization }): ReactElement {
  const update = useUpdateOrg();
  const { show } = useNotices();
  return (
    <Card title="Privacy" flush>
      <div className="setting">
        <span className="k">Count people by name</span>
        <div className="v">
          {org.canAdminister ? (
            <Switch
              aria-label="Count people by name"
              label={org.peopleAnalytics ? 'On' : 'Off'}
              checked={org.peopleAnalytics}
              disabled={update.isPending}
              onChange={(e) =>
                update.mutate(
                  { peopleAnalytics: e.target.checked },
                  {
                    onSuccess: (next) =>
                      show({
                        tone: 'success',
                        text: next.peopleAnalytics
                          ? 'Analytics now lists each member. Tell them — it is their data.'
                          : 'Analytics no longer lists anyone by name.',
                      }),
                    onError: (err) => show({ tone: 'failure', text: err.message }),
                  },
                )
              }
            />
          ) : (
            <span className="t-ui">{org.peopleAnalytics ? 'On' : 'Off'}</span>
          )}
          <span className="why">
            when on, administrators see each member’s number of meetings, time in calls, and open
            and done commitments in Analytics. Never guests, never talk time, never a score, never
            anything anybody said. In some countries this needs the agreement of a works council.
          </span>
        </div>
      </div>
      <div className="setting">
        <span className="k">Connected assistants</span>
        <div className="v">
          {org.canAdminister ? (
            <Switch
              aria-label="Connected assistants"
              label={org.mcpEnabled ? 'Allowed' : 'Not allowed'}
              checked={org.mcpEnabled}
              disabled={update.isPending}
              onChange={(e) =>
                update.mutate(
                  { mcpEnabled: e.target.checked },
                  {
                    onSuccess: (next) =>
                      show({
                        tone: 'success',
                        text: next.mcpEnabled
                          ? 'Assistants can read what the person connecting them can read.'
                          : 'Assistants can no longer read this organisation’s meetings.',
                      }),
                    onError: (err) => show({ tone: 'failure', text: err.message }),
                  },
                )
              }
            />
          ) : (
            <span className="t-ui">{org.mcpEnabled ? 'Allowed' : 'Not allowed'}</span>
          )}
          <span className="why">
            an assistant somebody connects — Claude, or another MCP client — reads only what that
            person can read, on their instruction, and what it receives is governed by its maker’s
            terms rather than ours. Turning this off refuses every such request for the whole
            organisation, whatever anybody has already connected.
          </span>
        </div>
      </div>
    </Card>
  );
}

/**
 * The end of a workspace. Typed, because a button that ends a company's record
 * should be impossible to press by accident and unmistakable on purpose.
 */
function DeleteWorkspace({ org }: { org: Organization }): ReactElement {
  const remove = useDeleteWorkspace();
  const { show } = useNotices();
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const close = () => {
    setOpen(false);
    setTyped('');
  };
  return (
    <Card title="Delete this organisation" flush>
      <div className="setting">
        <span className="k" />
        <div className="v">
          <Button variant="danger" onClick={() => setOpen(true)}>
            Delete {org.name}…
          </Button>
          <span className="why">
            everything: every meeting recorded here — notes, transcripts, video — its settings, its
            member list, and the accounts of everyone in it, including yours. Anybody who also
            belongs to another organisation keeps their account and loses only this one’s meetings.
          </span>
        </div>
      </div>
      {open ? (
        <Modal
          title={`Delete ${org.name}`}
          onClose={close}
          foot={
            <>
              <Button onClick={close}>Keep it</Button>
              <Button
                variant="danger"
                disabled={typed.trim() !== org.name}
                working={remove.isPending ? 'Deleting…' : false}
                onClick={() =>
                  remove.mutate(undefined, {
                    onSuccess: () => {
                      window.location.assign('/');
                    },
                    onError: (err) => show({ tone: 'failure', text: err.message }),
                  })
                }
              >
                Delete the organisation
              </Button>
            </>
          }
        >
          <p className="t-body">
            This deletes every meeting recorded in {org.name} for everyone, with its transcript, its
            video and what was learned from it — in the shared spaces and in people’s own. It cannot
            be undone, and we keep no copy beyond backups that expire within 30 days.
          </p>
          <p className="t-body">
            <strong>It also deletes the account of everyone in {org.name}, including yours.</strong>{' '}
            You will be signed out and there will be nothing to sign back in to. Anyone who also
            belongs to another organisation keeps their account, and loses only what was recorded
            here.
          </p>
          <Field
            aria-label={`Type ${org.name} to confirm`}
            placeholder={`Type “${org.name}” to confirm`}
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            style={{ marginTop: 12, width: '100%' }}
          />
        </Modal>
      ) : null}
    </Card>
  );
}
